import { motion } from "framer-motion";
import { Camera, Check, Minus, Plus, X } from "lucide-react";
import { fmt } from "./demoData";

export const EASE = [0.16, 1, 0.3, 1];

export const BTN_PRIMARY =
  "group inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white bg-gradient-to-r from-brand-orange to-brand-blue shadow-[0_0_30px_-6px_rgba(255,107,0,0.7)] hover:shadow-[0_0_40px_-4px_rgba(0,122,255,0.8)] hover:scale-[1.03] transition-all duration-300";
export const BTN_SECONDARY =
  "inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white/80 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] hover:text-white transition-all duration-300";
export const BTN_SMALL =
  "inline-flex items-center justify-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white/80 border border-white/15 bg-white/[0.02] hover:bg-white/[0.06] hover:text-white transition-all duration-300";
export const BTN_GHOST =
  "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold text-white/55 hover:text-white hover:bg-white/[0.06] transition-colors duration-300";

/** Karte ohne eigene Füllung, nur über den Rahmen definiert. */
export const Panel = ({ className = "", children }) => (
  <div className={`relative rounded-3xl border border-white/10 backdrop-blur-xl ${className}`}>
    {children}
  </div>
);

export const Kicker = ({ className = "", children }) => (
  <span className={`block text-[11px] uppercase tracking-[0.2em] font-bold text-white/40 ${className}`}>
    {children}
  </span>
);

/**
 * Herkunft einer Zahl. Drei sichtbare Stufen: gemessen, Angabe, Schätzung.
 * Das Kamera-Symbol zeigt, dass eine Angabe mit Foto oder Dokument belegt ist.
 */
export const SourceBadge = ({ kind, by, proof }) => {
  const label =
    kind === "gemessen" ? "gemessen" : kind === "schaetzung" ? "Schätzung" : by ? `Angabe ${by}` : "Angabe";
  const tone =
    kind === "gemessen"
      ? "border-brand-blue/40 bg-brand-blue/10 text-[#6FB1FF]"
      : kind === "schaetzung"
      ? "border-dashed border-white/25 text-white/55"
      : "border-white/15 bg-white/[0.04] text-white/70";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold tracking-wide whitespace-nowrap ${tone}`}
    >
      {label}
      {proof && <Camera className="w-3 h-3" aria-label="mit Foto oder Dokument belegt" />}
    </span>
  );
};

/** Segmentierter Umschalter mit neutraler weißer Pille als Aktiv-Zustand. */
export const Segmented = ({ options, value, onChange, layoutId, size = "md", label }) => (
  <div
    role="group"
    aria-label={label}
    className="inline-flex flex-wrap items-center gap-1 rounded-2xl border border-white/10 bg-white/[0.03] p-1"
  >
    {options.map((o) => {
      const active = o.value === value;
      const Icon = o.icon;
      return (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          aria-pressed={active}
          className={`relative rounded-xl font-semibold transition-colors duration-300 ${
            size === "sm" ? "px-3.5 py-1.5 text-xs" : "px-4 sm:px-5 py-2 text-sm"
          } ${active ? "text-white" : "text-white/50 hover:text-white/80"}`}
        >
          {active && (
            <motion.span
              layoutId={layoutId}
              className="absolute inset-0 rounded-xl bg-white/[0.08] border border-white/15"
              transition={{ type: "spring", stiffness: 380, damping: 32 }}
            />
          )}
          <span className="relative inline-flex items-center gap-2 whitespace-nowrap">
            {/* Symbole erst ab Tablet-Breite, damit die Pillen mobil in eine Zeile passen. */}
            {Icon && <Icon className={`hidden sm:block ${size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4"}`} />}
            {o.label}
          </span>
        </button>
      );
    })}
  </div>
);

export const Switch = ({ checked, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    aria-label={label}
    onClick={() => onChange(!checked)}
    className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full border transition-colors duration-300 ${
      checked
        ? "border-transparent bg-origin-border bg-gradient-to-r from-brand-orange to-brand-blue"
        : "border-white/15 bg-white/[0.06]"
    }`}
  >
    <motion.span
      layout
      transition={{ type: "spring", stiffness: 500, damping: 34 }}
      className={`inline-block h-4 w-4 rounded-full ${checked ? "ml-[22px] bg-white" : "ml-1 bg-white/60"}`}
    />
  </button>
);

export const QtyStepper = ({ value, min, max, step = 1, onChange, label }) => (
  <div className="inline-flex items-center gap-0.5 rounded-full border border-white/10 bg-white/[0.03] p-1">
    <button
      type="button"
      aria-label={`${label}: weniger`}
      onClick={() => onChange(Math.max(min, value - step))}
      className="flex h-7 w-7 items-center justify-center rounded-full text-white/60 hover:bg-white/[0.08] hover:text-white transition-colors"
    >
      <Minus className="w-3.5 h-3.5" />
    </button>
    <span className="min-w-[1.75rem] text-center text-sm font-semibold tabular-nums">{value}</span>
    <button
      type="button"
      aria-label={`${label}: mehr`}
      onClick={() => onChange(Math.min(max, value + step))}
      className="flex h-7 w-7 items-center justify-center rounded-full text-white/60 hover:bg-white/[0.08] hover:text-white transition-colors"
    >
      <Plus className="w-3.5 h-3.5" />
    </button>
    <span className="pl-1 pr-2.5 text-xs text-white/45">{label}</span>
  </div>
);

export const GradientCheck = ({ size = "md" }) => (
  <span
    className={`flex items-center justify-center shrink-0 rounded-full bg-gradient-to-br from-brand-orange to-brand-blue ${
      size === "lg" ? "w-6 h-6" : "w-5 h-5"
    }`}
  >
    <Check className={size === "lg" ? "w-3.5 h-3.5 text-white" : "w-3 h-3 text-white"} strokeWidth={3} />
  </span>
);

export const ProgressLine = ({ value, tone = "gradient" }) => (
  <div className="h-1.5 w-full rounded-full bg-white/[0.07] overflow-hidden">
    <motion.div
      className={`h-full rounded-full ${
        tone === "warn" ? "bg-brand-orange/80" : "bg-gradient-to-r from-brand-orange to-brand-blue"
      }`}
      initial={{ width: 0 }}
      animate={{ width: `${Math.min(1, Math.max(0, value)) * 100}%` }}
      transition={{ duration: 0.9, ease: EASE }}
    />
  </div>
);

/** Ring für den Erfüllungsgrad. */
export const Ring = ({ value, size = 144 }) => {
  const r = 52;
  const c = 2 * Math.PI * r;
  return (
    <svg viewBox="0 0 120 120" width={size} height={size} aria-hidden="true">
      <defs>
        <linearGradient id="mg-demo-ring" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FF6B00" />
          <stop offset="100%" stopColor="#007AFF" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r={r} stroke="rgba(255,255,255,0.08)" strokeWidth="9" fill="none" />
      <motion.circle
        cx="60"
        cy="60"
        r={r}
        stroke="url(#mg-demo-ring)"
        strokeWidth="9"
        fill="none"
        strokeLinecap="round"
        strokeDasharray={c}
        initial={{ strokeDashoffset: c }}
        animate={{ strokeDashoffset: c * (1 - Math.min(1, Math.max(0, value))) }}
        transition={{ duration: 1.2, ease: EASE }}
        transform="rotate(-90 60 60)"
      />
    </svg>
  );
};

/**
 * Säulendiagramm. Die Spalten werden gestreckt (items-stretch), damit die
 * prozentualen Höhen der Säulen eine feste Bezugsgröße haben.
 */
export const ColumnChart = ({ items, height = 176, ariaLabel, markers = false }) => {
  const max = Math.max(1, ...items.map((i) => i.value ?? 0));
  return (
    <div role="img" aria-label={ariaLabel} className="flex items-stretch gap-1 sm:gap-1.5" style={{ height }}>
      {items.map((it, i) => {
        const pct = it.value != null ? Math.max(3, (it.value / max) * 100) : 0;
        return (
          <div key={it.key} className="flex-1 min-w-0 flex flex-col items-center gap-2" title={it.title}>
            <div className="relative w-full flex-1">
              {it.value != null ? (
                <>
                  <motion.div
                    className={`absolute bottom-0 inset-x-0 rounded-t-md ${
                      it.highlight
                        ? "bg-gradient-to-t from-brand-orange/50 to-brand-blue/80"
                        : "bg-white/[0.12]"
                    } ${it.entered ? "ring-1 ring-white/70" : ""}`}
                    initial={{ height: 0 }}
                    animate={{ height: `${pct}%` }}
                    transition={{ duration: 0.9, ease: EASE, delay: i * 0.025 }}
                  />
                  <motion.span
                    style={{ bottom: `${pct}%` }}
                    className="absolute inset-x-0 mb-1 hidden sm:block text-center text-[10px] font-semibold tabular-nums text-white/55"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 + i * 0.025 }}
                  >
                    {fmt(it.value)}
                  </motion.span>
                </>
              ) : it.state === "planned" ? (
                <div className="absolute bottom-0 inset-x-0 h-[14%] rounded-t-md border border-dashed border-white/15" />
              ) : it.state === "cancelled" ? (
                <div className="absolute bottom-0 inset-x-0 flex justify-center pb-1">
                  <X className="w-3.5 h-3.5 text-white/35" />
                </div>
              ) : null}
            </div>
            <span
              className={`text-[10px] leading-none whitespace-nowrap ${
                it.entered ? "text-white font-semibold" : "text-white/40"
              }`}
            >
              {it.label}
            </span>
            {markers && <span className="h-3.5 flex items-center text-white/45">{it.marker}</span>}
          </div>
        );
      })}
    </div>
  );
};

export const HBars = ({ items }) => {
  const max = Math.max(1, ...items.map((i) => i.value));
  return (
    <ul className="space-y-5">
      {items.map((it, i) => (
        <li key={it.id}>
          <div className="flex items-baseline justify-between gap-3">
            <span className="text-sm text-white/75">{it.label}</span>
            <span className="text-sm font-semibold tabular-nums text-white">{fmt(it.value)}</span>
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-white/[0.07] overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-gradient-to-r from-brand-orange to-brand-blue"
              initial={{ width: 0 }}
              animate={{ width: `${(it.value / max) * 100}%` }}
              transition={{ duration: 0.9, ease: EASE, delay: i * 0.06 }}
            />
          </div>
          {it.hint && <p className="mt-1.5 text-xs text-white/35">{it.hint}</p>}
        </li>
      ))}
    </ul>
  );
};
